import { Box, Card, Divider, Typography } from '@mui/material';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { List } from 'react-window';
import { useAtomValue } from 'jotai';
import { Product_Sub_Family_Name_Enum } from '@kheops/graphql';
import useCurrentBreakpoint from '../../hooks/useCurrentBreakpoint';
import { companyPackagingsAtom } from '../state';
import { insensitiveStringIncludes } from '../../common/utils/common.utils';
import useSearchProducts from '../../products/common/useSearchProducts';
import SearchInput from '../../common/components/SearchInput';
import ProductSubFamilyFilter from '../../products/common/ProductSubFamilyFilter';
import PackagingsPricesFormRow from './PackagingsPricesFormRow';

export default function PackagingsPricesForm(): React.JSX.Element {
  const { t } = useTranslation(['common', 'products', 'price']);
  const breakpoint = useCurrentBreakpoint();
  const companyPackagings = useAtomValue(companyPackagingsAtom);
  const [filteredProducts, setFilteredProducts] = useState(companyPackagings);

  const productFamiliesOptions = useMemo((): Product_Sub_Family_Name_Enum[] => {
    const options = new Set<Product_Sub_Family_Name_Enum>();

    companyPackagings.forEach(({ product }) => options.add(product.sub_family as Product_Sub_Family_Name_Enum));

    return [...options.values()];
  }, [companyPackagings]);

  const virtualItemSize = useMemo((): number => {
    switch (breakpoint) {
      case 'xs':
        return 272;
      case 'sm':
        return 256;
      case 'md':
        return 256;
      default:
        return 116;
    }
  }, [breakpoint]);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const VirtualRow: any = useCallback(({ index, style }: any) => (
    <div style={style}>
      <Card
        sx={{
          mt: 1.5,
          backgroundColor: 'surfaceContainerLow.main',
          p: 1.5,
          mr: 1,
          borderRadius: 3,
        }}
      >
        <PackagingsPricesFormRow packaging={filteredProducts[index]} />
      </Card>
    </div>
  ), [filteredProducts]);

  const handleProductSearchChange = useCallback((productFamilies: Product_Sub_Family_Name_Enum[], query: string): void => {
    const newFilteredProducts = companyPackagings.filter((packaging) => {
      const matchFamilies = !productFamilies.length || productFamilies.includes(packaging.product.sub_family as Product_Sub_Family_Name_Enum);
      const matchSearch = !query || insensitiveStringIncludes(packaging.product.name, query) || packaging.base_unit.gtin?.includes(query);

      return matchFamilies && matchSearch;
    });

    setFilteredProducts(newFilteredProducts);
  }, [companyPackagings]);

  useEffect(() => {
    setFilteredProducts(companyPackagings);
  }, [companyPackagings]);

  const {
    query,
    setQuery,
    productFamilyMap,
    setProductFamilies,
    selectedProductFamilyMap,
  } = useSearchProducts({ onChange: handleProductSearchChange, productFamiliesOptions });

  return (
    <>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 1 }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: {
              xs: 'column',
              sm: 'row',
            },
            alignItems: {
              xs: 'flex-start',
              sm: 'center',
            },
            gap: 1,
            mt: 0.5,
            mb: { xs: 0, lg: 2 },
          }}
        >
          <SearchInput
            value={query}
            onChange={setQuery}
            sx={{
              width: {
                xs: '100%',
                lg: 400,
              },
            }}
          />
          <Divider
            orientation="vertical"
            sx={{
              height: 24,
              display: {
                xs: 'none',
                sm: 'flex',
              },
            }}
          />
          <ProductSubFamilyFilter
            productFamilyMap={productFamilyMap}
            onChange={(values) => setProductFamilies(values.map(({ value }) => value))}
            selectedProductFamilyMap={selectedProductFamilyMap}
          />
        </Box>
        <Box
          sx={{
            display: {
              xs: 'none',
              lg: 'flex',
            },
            px: 1.5,
            mr: 1,
            gap: 1,
          }}
        >
          <Box sx={{ flex: '1 1 auto' }}>
            <Typography variant="bodySmall">
              {t('products:base_unit_one')}
            </Typography>
          </Box>
          <Box sx={{ width: 124, pr: 0.5 }}>
            <Typography variant="bodySmall" sx={{ textAlign: 'right' }}>
              {t('price:packaging_price_without_tax')}
            </Typography>
          </Box>
          <Box sx={{ width: 124, pr: 0.5, display: 'flex', justifyContent: 'end' }}>
            <Typography variant="bodySmall" sx={{ textAlign: 'right', width: 102 }}>
              {t('price:catalog_price_without_tax')}
            </Typography>
          </Box>
          <Box sx={{ width: 124, pr: 0.5 }}>
            <Typography variant="bodySmall" sx={{ textAlign: 'right' }}>
              {t('price:suggest_retail_price_tax_included')}
            </Typography>
          </Box>
        </Box>
      </Box>
      <List
        rowComponent={VirtualRow}
        rowCount={filteredProducts.length}
        rowHeight={virtualItemSize}
        rowProps={{}}
        overscanCount={5}
      />
    </>
  );
}
